import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
    BatchSpanProcessor,
    SpanProcessor,
    ReadableSpan,
    Span as SdkSpan,
} from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
    ATTR_SERVICE_NAME,
    SEMRESATTRS_DEPLOYMENT_ENVIRONMENT,
} from "@opentelemetry/semantic-conventions";
import { context, Context } from "@opentelemetry/api";
import {
    LoggerProvider,
    BatchLogRecordProcessor,
} from "@opentelemetry/sdk-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { logs, SeverityNumber } from "@opentelemetry/api-logs";

interface UserAttributes {
    userId?: string;
    email?: string;
    companyId?: string;
    companyName?: string;
    workspaceId?: string;
    djangoSessionId?: string;
}

class UserSpanProcessor implements SpanProcessor {
    constructor(
        private readonly inner: SpanProcessor,
        private readonly getUserAttrs: () => UserAttributes
    ) {}

    onStart(span: SdkSpan, parentContext: Context): void {
        const {
            userId,
            email,
            companyId,
            companyName,
            workspaceId,
            djangoSessionId,
        } = this.getUserAttrs();
        if (userId) span.setAttribute("testbox.user_id", userId);
        if (email) span.setAttribute("testbox.user_email", email);
        if (companyId) span.setAttribute("testbox.company_id", companyId);
        if (companyName) span.setAttribute("testbox.company_name", companyName);
        if (workspaceId) span.setAttribute("testbox.workspace_id", workspaceId);
        if (djangoSessionId)
            span.setAttribute("testbox.django_session_id", djangoSessionId);
        if (typeof window !== "undefined") {
            span.setAttribute(
                "app.route",
                window.location.pathname + window.location.search
            );
        }
        this.inner.onStart(span, parentContext);
    }

    onEnd(span: ReadableSpan): void {
        this.inner.onEnd(span);
    }

    shutdown(): Promise<void> {
        return this.inner.shutdown();
    }

    forceFlush(): Promise<void> {
        return this.inner.forceFlush();
    }
}

export class OtelService {
    private static instance: OtelService;
    private initialized = false;
    private userAttrs: UserAttributes = {};

    private constructor() {}

    static getInstance(): OtelService {
        if (!OtelService.instance) {
            OtelService.instance = new OtelService();
        }
        return OtelService.instance;
    }

    setUser(attrs: UserAttributes): void {
        this.userAttrs = attrs;
    }

    initialize(config: {
        endpoint: string;
        headers?: string | null;
        deploymentEnvironment?: string | null;
    }): void {
        if (this.initialized) {
            return;
        }

        if (!config.endpoint) {
            return;
        }

        const otlpHeaders: Record<string, string> = {};
        (config.headers ?? "").split(",").forEach((pair) => {
            const idx = pair.indexOf("=");
            if (idx > 0) {
                const key = pair.slice(0, idx).trim();
                const value = pair.slice(idx + 1).trim();
                if (key && value) otlpHeaders[key] = value;
            }
        });

        try {
            const resource = resourceFromAttributes({
                [ATTR_SERVICE_NAME]: "testbox-frontend",
                // TODO: SEMATTRS_DEPLOYMENT_ENVIRONMENT deprecated in favour of ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
                // but the switch has to be done across all services at once to maintain consistency.
                [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]:
                    config.deploymentEnvironment ?? "unknown",
            });

            const exporter = new OTLPTraceExporter({
                url: `${config.endpoint}/v1/traces`,
                headers: otlpHeaders,
            });

            const provider = new WebTracerProvider({
                resource,
                spanProcessors: [
                    new UserSpanProcessor(
                        new BatchSpanProcessor(exporter),
                        () => this.userAttrs
                    ),
                ],
            });

            provider.register();

            registerInstrumentations({
                instrumentations: [
                    new DocumentLoadInstrumentation(),
                    new XMLHttpRequestInstrumentation({
                        propagateTraceHeaderCorsUrls: [
                            /https:\/\/.*\.testbox\.com/,
                            /https:\/\/.*\.toybox\.company/,
                            /http:\/\/localhost:8000/,
                        ],
                        ignoreUrls: [/otel\.testbox\.com/, /signoz\.cloud/],
                    }),
                    new FetchInstrumentation({
                        propagateTraceHeaderCorsUrls: [
                            /https:\/\/.*\.testbox\.com/,
                            /https:\/\/.*\.toybox\.company/,
                            /http:\/\/localhost:8000/,
                        ],
                        ignoreUrls: [/otel\.testbox\.com/, /signoz\.cloud/],
                    }),
                ],
            });

            const logExporter = new OTLPLogExporter({
                url: `${config.endpoint}/v1/logs`,
                headers: otlpHeaders,
            });
            const loggerProvider = new LoggerProvider({
                resource,
                processors: [new BatchLogRecordProcessor(logExporter)],
            });
            logs.setGlobalLoggerProvider(loggerProvider);

            const otelLogger = loggerProvider.getLogger("testbox-frontend");
            const severityMap: Record<string, SeverityNumber> = {
                log: SeverityNumber.INFO,
                info: SeverityNumber.INFO,
                warn: SeverityNumber.WARN,
                error: SeverityNumber.ERROR,
                debug: SeverityNumber.DEBUG,
            };
            (["log", "info", "warn", "error", "debug"] as const).forEach(
                (level) => {
                    const orig = console[level].bind(console);
                    (console as any)[level] = (...args: unknown[]) => {
                        orig(...args);
                        const {
                            userId,
                            email,
                            companyId,
                            companyName,
                            workspaceId,
                            djangoSessionId,
                        } = this.userAttrs;
                        const logAttrs: Record<string, string> = {};
                        if (userId) logAttrs["testbox.user_id"] = userId;
                        if (email) logAttrs["testbox.user_email"] = email;
                        if (companyId)
                            logAttrs["testbox.company_id"] = companyId;
                        if (companyName)
                            logAttrs["testbox.company_name"] = companyName;
                        if (workspaceId)
                            logAttrs["testbox.workspace_id"] = workspaceId;
                        if (djangoSessionId)
                            logAttrs["testbox.django_session_id"] =
                                djangoSessionId;
                        otelLogger.emit({
                            severityNumber: severityMap[level],
                            severityText: level.toUpperCase(),
                            body: args
                                .map((a) =>
                                    typeof a === "string"
                                        ? a
                                        : JSON.stringify(a)
                                )
                                .join(" "),
                            context: context.active(),
                            ...(Object.keys(logAttrs).length > 0 && {
                                attributes: logAttrs,
                            }),
                        });
                    };
                }
            );

            this.initialized = true;
            console.log("OpenTelemetry initialized successfully");
        } catch (error) {
            console.error("Failed to initialize OpenTelemetry:", error);
        }
    }

    isInitialized(): boolean {
        return this.initialized;
    }
}

export default OtelService;
